import React, { useState, useEffect } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import { useNavigate } from 'react-router-dom';
import url from "../../config.js";
import { startLogin } from "../../auth/oidc.js";
import intelogicon from '../../assets/icons/intelog_new_png.png';
import './login.scss';

const domain = url.domain;
// When false (e.g. GCP), hide the username/password login and show only SSO (Keycloak + Google).
const passwordLogin = url.passwordLogin !== false;

function Login() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    username: '',
    password: '',
    loginPage: true,
    loginFailedmessage: '',
    email: '',
    emailValid: false,
    usernameValid: false,
    passwordValid: false,
    tenantValid: false,
    emailErrorMessage: '',
    usernameErrorMessage: '',
    passwordErrorMessage: '',
    tenantErrorMessage: '',
    firstName: '',
    firstNameValid: false,
    firstNameErrorMessage: '',
    middleName: '',
    lastName: '',
    tenantId: '',
    platformRole: 'user',
    tenantRole: 'developer',
    invalidUser: false,
    registrationFailed: false,
    registrationFailedMessage: '',
    signUpProcess: false,
    logProcessingUser: false,
    isLoading: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    fetch(domain + '/api/v1/tenants')
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setTenants(data);
          // Auto-select NJCLabs tenant by default
          const njc = data.find(t => t.name?.toLowerCase().includes('njc') || t.name?.toLowerCase().includes('njclabs'));
          if (njc) setState(prev => ({ ...prev, tenantId: njc._id, tenantValid: true }));
          else if (data.length > 0) setState(prev => ({ ...prev, tenantId: data[0]._id, tenantValid: true }));
        }
      })
      .catch(() => {});
  }, []);

  const handleChange = e => {
    e.persist();
    if (e.target.name === 'email') {
      let emailValidtemp = e.target.value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
      setState(prevState => ({
        ...prevState,
        [e.target.name]: e.target.value,
        emailErrorMessage: (emailValidtemp ? '' : 'Please enter a valid email'),
        [e.target.name + 'Valid']: !!emailValidtemp
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        [e.target.name]: e.target.value,
        [e.target.name + 'ErrorMessage']: '',
        [e.target.name + 'Valid']: true
      }));
    }
  };

  const register = () => {
    console.log('[SignUp] register() called → username:', state.username, '| email:', state.email, '| tenantId:', state.tenantId, '| platformRole:', state.platformRole);
    let emailValidtemp = state.email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
    let emailValidator = emailValidtemp ? '' : ' is invalid';

    if (emailValidator === '') {
      setState(prevState => ({ ...prevState, emailValid: true }));
    } else {
      setState(prevState => ({ ...prevState, emailErrorMessage: "Please enter a valid email" }));
    }

    if (state.password === '') {
      setState(prevState => ({ ...prevState, passwordValid: false, passwordErrorMessage: "Please enter a password" }));
    }

    if (state.username === '') {
      setState(prevState => ({ ...prevState, usernameValid: false, usernameErrorMessage: "Please enter a username" }));
    }

    if (!state.firstName.trim()) {
      setState(prevState => ({ ...prevState, firstNameValid: false, firstNameErrorMessage: "Please enter your first name" }));
    }

    if (state.emailValid && state.passwordValid && state.usernameValid && state.firstName.trim() !== '') {
      setState(prevState => ({ ...prevState, isLoading: true }));

      var postObj = {
        "username": state.username,
        "password": state.password,
        "email": state.email,
        "roles": ["user", "moderator"],
        "firstName": state.firstName,
        "middleName": state.middleName,
        "lastName": state.lastName,
        "tenantId": state.tenantId,
        "platformRole": state.platformRole,
        "tenantRole": state.tenantRole
      };

      console.log('[SignUp] → POST', domain + '/api/auth/register', '| body:', postObj);
      const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postObj)
      };

      fetch(domain + '/api/auth/register', requestOptions)
        .then(response => response.json().catch(() => ({})).then(data => ({ status: response.status, ok: response.ok, data })))
        .then(({ status, ok, data }) => {
          // The backend returns 400 as { message: "Failed to register user", reason: <real cause> }.
          // Log status + reason + full body so the actual failure (e.g. Keycloak provisioning,
          // duplicate email/username, db error) is visible, not just the generic message.
          console.log('[SignUp] ← response status:', status, '| ok:', ok, '| body:', data);
          setState(prevState => ({ ...prevState, isLoading: false }));
          const failureReason = data && (data.reason || data.message);
          if (ok && !failureReason) {
            console.log('[SignUp] registration succeeded');
            setState(prevState => ({ ...prevState, loginPage: true, signUpProcess: true }));
          } else {
            const msg = failureReason || `Registration failed (HTTP ${status}).`;
            console.error('[SignUp] registration failed →', msg, '| status:', status, '| full response:', data);
            setState(prevState => ({
              ...prevState,
              registrationFailed: true,
              registrationFailedMessage: msg
            }));
          }
        })
        .catch(err => {
          console.error('[SignUp] ← network/parse error:', err);
          setState(prevState => ({
            ...prevState,
            isLoading: false,
            registrationFailed: true,
            registrationFailedMessage: 'Registration failed — could not reach the server. Please try again.'
          }));
        });
    } else {
      console.warn('[SignUp] validation failed → emailValid:', state.emailValid, '| passwordValid:', state.passwordValid, '| usernameValid:', state.usernameValid);
    }
  };

  const loginFun = () => {
    console.log('[Login] loginFun() called → username:', state.username);
    setState(prevState => ({ ...prevState, isLoading: true }));

    var postObj = {
      "username": state.username,
      "password": state.password,
    };

    console.log('[Login] → POST', domain + '/api/auth/signin', '| username:', state.username);
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(postObj)
    };

    fetch(domain + '/api/auth/signin', requestOptions)
      .then(response => response.json())
      .then(data => {
        console.log('[Login] ← response:', data);
        setState(prevState => ({ ...prevState, isLoading: false }));

        if (data.permission === 'processing') {
          console.log('[Login] permission=processing → account pending approval');
          setState(prevState => ({
            ...prevState,
            loginPage: true,
            signUpProcess: true,
            logProcessingUser: true
          }));
        }
        else if (data.message !== 'User not found' && data.message !== 'Invalid Password!' && data.permission === 'granted') {
          // Store user data with proper pagePermissions extraction
          const userData = {
            id: data.id,
            username: data.username,
            email: data.email,
            roles: data.roles,
            permission: data.permission,
            tenantId: data.tenantId,
            platformRole: data.platformRole,
            tenantRole: data.tenantRole,
            pagePermissions: data.pagePermissions || {},
          };
          const _pp = data.pagePermissions || {};
          console.group('%c[Login] Permissions', 'color:#2EBFA5;font-weight:700');
          console.log('user:', data.username, '| roles:', data.roles, '| tenantId:', data.tenantId);
          console.table(_pp);
          console.log('granted:', Object.keys(_pp).filter(k => _pp[k] === true || _pp[k] === 'true'));
          console.log('raw pagePermissions:', _pp);
          if (Object.keys(_pp).length === 0) console.warn('[Login] ⚠️ pagePermissions is EMPTY — backend returned no permissions for this user');
          console.groupEnd();
          localStorage.setItem('token', data.accessToken);
          localStorage.setItem('user', JSON.stringify(userData));
          window.location.href = '/dashboard';
        } else {
          const rawMsg = data.message || '';
          const friendlyMsg = /user not found|invalid password/i.test(rawMsg)
            ? 'Invalid username or password.'
            : rawMsg || 'Invalid username or password.';
          console.warn('[Login] ❌ failed → message:', rawMsg);
          setState(prevState => ({
            ...prevState,
            invalidUser: true,
            loginFailedmessage: friendlyMsg
          }));
        }
      })
      .catch(err => {
        console.error('[Login] ← error:', err);
        setState(prevState => ({
          ...prevState,
          isLoading: false,
          invalidUser: true,
          loginFailedmessage: 'Login failed. Please try again.'
        }));
      });
  };

  const changeLoginFrom = () => {
    let page = !state.loginPage;
    setState(prevState => ({
      ...prevState,
      loginPage: page,
      invalidUser: false,
      registrationFailed: false,
      emailErrorMessage: '',
      usernameErrorMessage: '',
      passwordErrorMessage: ''
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (state.loginPage) {
        loginFun();
      } else {
        register();
      }
    }
  };

  const getFormBody = () => {
    if (state.loginPage) {
      return (
        <div className="form-container">
          <div className="form-header">
            <h2 className="form-title">Sign in to INTELOG</h2>
            <p className="form-subtitle">Welcome back! Please enter your details</p>
          </div>

          {passwordLogin && (
          <>
          <div className="form-group">
            <label className="form-label">Username</label>
            <TextField
              className="form-input"
              variant="outlined"
              required
              fullWidth
              id="username"
              placeholder="Enter your username"
              name="username"
              onChange={(e) => handleChange(e)}
              onKeyPress={handleKeyPress}
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="form-group">
            <div className="password-label-container">
              <label className="form-label">Password</label>
              <Link className="forgot-pwd" style={{ cursor: 'pointer' }} variant="body2" onClick={() => navigate('/forgot-password')}>
                Forgot password?
              </Link>
            </div>
            <TextField
              className="form-input"
              variant="outlined"
              required
              fullWidth
              name="password"
              placeholder="Enter your password"
              type={showPassword ? "text" : "password"}
              id="password"
              onChange={(e) => handleChange(e)}
              onKeyPress={handleKeyPress}
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(p => !p)}
                      edge="end"
                      style={{ color: '#6b849a' }}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          

          <FormControlLabel
            className="remember-me"
            control={<Checkbox value="remember" color="primary" />}
            label="Remember me"
          />

          <button
            type="button"
            onClick={() => loginFun()}
            className="form-button"
            disabled={state.isLoading}
          >
            {state.isLoading ? 'Signing In...' : 'Sign In'}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0', color: '#8aa0b6', fontSize: 12 }}>
            <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} />
            or
            <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.12)' }} />
          </div>
          </>
          )}

          <button
            type="button"
            onClick={() => startLogin()}
            style={{
              width: '100%',
              padding: '14px 24px',
              borderRadius: 8,
              background: 'transparent',
              border: '1px solid #2EBFA5',
              color: '#2EBFA5',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            Sign In
          </button>

          <button
            type="button"
            onClick={() => startLogin('google')}
            style={{
              width: '100%',
              padding: '14px 24px',
              marginTop: 12,
              borderRadius: 8,
              background: '#ffffff',
              border: '1px solid #dadce0',
              color: '#3c4043',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Sign in with Google
          </button>

          {/* SignUp is always available — even on SSO-only (GCP) builds. Registration
              provisions a Keycloak user, so after signing up the user logs in via the
              Keycloak "Sign In" button. (Not gated by passwordLogin.) */}
          <div className="switch-link">
            Don't have an account?{' '}
            <Link onClick={() => changeLoginFrom()} variant="body2" href="#">
              SignUp
            </Link>
          </div>

          {state.invalidUser && (
            <div className="error-message">
              {state.loginFailedmessage !== '' ? state.loginFailedmessage : "Invalid Username/Password"}
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="form-container">
          <div className="form-header">
            <h2 className="form-title">Create your account</h2>
            <p className="form-subtitle">Join the future of enterprise integration</p>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <label className="form-label">First name *</label>
              <TextField
                className="form-input"
                autoComplete="fname"
                name="firstName"
                variant="outlined"
                required
                placeholder="Enter first name"
                fullWidth
                id="firstName"
                onChange={(e) => handleChange(e)}
              />
              {state.firstNameErrorMessage && (
                <div className="field-error">{state.firstNameErrorMessage}</div>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="form-label">Middle name</label>
              <TextField
                variant="outlined"
                className="form-input"
                fullWidth
                id="middleName"
                placeholder="Enter middle name"
                name="middleName"
                onChange={(e) => handleChange(e)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="form-label">Last name</label>
              <TextField
                variant="outlined"
                className="form-input"
                fullWidth
                id="lastName"
                placeholder="Enter last name"
                name="lastName"
                autoComplete="lname"
                onChange={(e) => handleChange(e)}
              />
            </Grid>
            <Grid item xs={12}>
              <label className="form-label">Username *</label>
              <TextField
                variant="outlined"
                className="form-input"
                required
                fullWidth
                id="reg-username"
                name="username"
                placeholder="Enter username"
                autoComplete="username"
                onChange={(e) => handleChange(e)}
                autoFocus
              />
              {state.usernameErrorMessage && (
                <div className="field-error">{state.usernameErrorMessage}</div>
              )}
            </Grid>
            <Grid item xs={12}>
              <label className="form-label">Email *</label>
              <TextField
                variant="outlined"
                className="form-input"
                required
                fullWidth
                id="email"
                name="email"
                placeholder="Enter email"
                autoComplete="email"
                onChange={(e) => handleChange(e)}
              />
              {state.emailErrorMessage && (
                <div className="field-error">{state.emailErrorMessage}</div>
              )}
            </Grid>
            <Grid item xs={12}>
              <label className="form-label">Password *</label>
              <TextField
                variant="outlined"
                required
                fullWidth
                name="password"
                className="form-input"
                type="password"
                id="reg-password"
                placeholder="Enter password"
                autoComplete="current-password"
                onChange={(e) => handleChange(e)}
                onKeyPress={handleKeyPress}
              />
              {state.passwordErrorMessage && (
                <div className="field-error">{state.passwordErrorMessage}</div>
              )}
            </Grid>
            {/* Tenant, Platform Role, Tenant Role — hidden; defaults set automatically */}
            {/* tenantId: auto-selected as NJCLabs | platformRole: user | tenantRole: developer */}
          </Grid>

          <button
            type="button"
            onClick={() => register()}
            className="form-button"
            disabled={state.isLoading}
          >
            {state.isLoading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="switch-link">
            Already have an account?{' '}
            <Link variant="body2" href="#" onClick={() => changeLoginFrom()}>
              Sign In
            </Link>
          </div>

          {state.registrationFailed && (
            <div className="error-message">
              {state.registrationFailedMessage}
            </div>
          )}
        </div>
      );
    }
  };

  const signUpConfirmBody = () => {
    return (
      <div className="success-container">
        <div className="success-icon-container">
          <div className="success-icon-circle">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        <div className="success-message-box">
          {state.logProcessingUser ? (
            <>
              <h3 className="success-message-title">
                Request Pending
              </h3>
              <p className="success-message-text">
                Your account is currently on the waiting list. You will be notified once access becomes available.
              </p>
            </>
          ) : (
            <>
              <h3 className="success-message-title">
                Thanks for signing up!
              </h3>
              <p className="success-message-text">
                You've been successfully added to our waiting list. We'll notify you as soon as access becomes available.
              </p>
            </>
          )}
        </div>

        <div className="additional-info">
          <p className="follow-updates-text">
            In the meantime, follow us for updates
          </p>

          <div className="social-icons">
            <a
              href="https://www.youtube.com/@njclabs9809"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-btn"
              aria-label="Subscribe to our YouTube channel"
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/njclabs"
              target="_blank"
              rel="noopener noreferrer"
              className="social-icon-btn"
              aria-label="Follow us on LinkedIn"
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setState(prev => ({ ...prev, signUpProcess: false, loginPage: true }))}
          className="back-button"
        >
          Back to Sign In
        </button>
      </div>
    );
  };

  return (
    <div className="login-root">
      <CssBaseline />

      {/* Split Layout */}
      <div className="login-split-container">
        {/* Left Side - Branding */}
        <div className="login-left-side">
          <div className="branding-content">
            <div className="logo-wrapper">
              <img
                src={intelogicon}
                alt="INTELOG"
                className="brand-logo"
              />
            </div>

            <div className="quote-section">
              <h1 className="brand-headline">
                Transform Integration Through Agentic AI
              </h1>
              <p className="brand-description">
                INTELOG revolutionizes enterprise integration by seamlessly converting legacy systems to modern applications including MuleSoft, Spring Boot, .Net, and more using cutting-edge AI technology and intelligent design agents.
              </p>

              <div className="features-list">
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <strong>AI-generated</strong>
                    <span>Describe your integration in plain language. Intelog designs the architecture, generates the code, and validates it before anything touches production.</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <strong>Human-governed</strong>
                    <span>Your team reviews and approves at every critical gate. The AI does the heavy lifting while your architects stay in control. Always.</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="feature-text">
                    <strong>Smarter every time</strong>
                    <span>Every successful enterprise AI integration is captured and learned from. The platform gets better with every deployment for every customer.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="login-right-side">
          <div className="form-wrapper">
            {!state.signUpProcess ? getFormBody() : signUpConfirmBody()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;